import { useState, useRef, type ChangeEvent, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import BackgroundLayer from "../components/BackgroundLayer";
import StatusBar from "../components/StatusBar";
import {
  uploadFilingPdf,
  parseFilingPdf,
  type ParseFilingAsyncResult,
} from "../lib/filings";

/**
 * UploadFiling — the v0 ingestion surface.
 *
 * Flow:
 *   idle → selected → uploading → parsing → preview → ingesting → done
 *                                              ↑                    ↓
 *                                          (cancel)          (navigate)
 *
 * Steps 1–2 are non-destructive (file in storage, no DB writes).
 * Step 3 happens only on user Confirm — the v0 "draft → approve → send"
 * gate, applied to filing ingestion as well as strike artifacts.
 */

type FlowState =
  | { kind: "idle" }
  | { kind: "selected"; file: File }
  | { kind: "uploading"; file: File }
  | { kind: "parsing" }
  | { kind: "preview"; parsed: ParseFilingAsyncResult }
  | { kind: "error"; message: string };

function UploadFiling() {
  const navigate = useNavigate();
  const [state, setState] = useState<FlowState>({ kind: "idle" });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function pickFile(file: File) {
    if (file.type !== "application/pdf") {
      setState({ kind: "error", message: "Only PDF files are supported." });
      return;
    }
    if (file.size > 52 * 1024 * 1024) {
      setState({ kind: "error", message: "File exceeds the 50 MB limit." });
      return;
    }
    setState({ kind: "selected", file });
  }

  function onFileInput(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) pickFile(file);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) pickFile(file);
  }

  async function runUploadAndParse(file: File) {
    setState({ kind: "uploading", file });
    let storagePath: string;
    try {
      storagePath = await uploadFilingPdf(file);
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
      return;
    }

    setState({ kind: "parsing" });
    try {
      // parse-filing now commits meta synchronously and kicks off
      // paragraph extraction in the background. Returns case_id + job_id
      // immediately.
      const parsed = await parseFilingPdf(storagePath);
      setState({ kind: "preview", parsed });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  function goToWarRoom(caseId: string) {
    navigate(`/case/${caseId}`);
  }

  function reset() {
    setState({ kind: "idle" });
  }

  return (
    <div className="relative min-h-screen text-bone">
      <BackgroundLayer variant="modes" overlay="heavy" />
      <StatusBar />

      <main className="relative z-10 mx-auto max-w-5xl px-6 py-12">
        <header className="mb-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute hover:text-bone"
          >
            ← back to command surface
          </button>
          <p className="mt-6 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">
            // ingestion // the eater
          </p>
          <h1
            className="mt-2 font-display text-3xl font-light leading-tight tracking-tight text-bone md:text-4xl"
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.7)" }}
          >
            Feed it the filing.
          </h1>
          <p className="mt-3 max-w-2xl font-display text-sm leading-relaxed text-bone-dim">
            Drop a complaint, answer, motion, or order. The Eater will
            parse the case caption, paragraph structure, and causes of
            action. You review what it extracted before anything writes
            to the corpus.
          </p>
        </header>

        {state.kind === "idle" || state.kind === "selected" ? (
          <DropZone
            file={state.kind === "selected" ? state.file : null}
            dragOver={dragOver}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            onParse={() =>
              state.kind === "selected" && runUploadAndParse(state.file)
            }
            onClear={reset}
          />
        ) : null}

        {state.kind === "uploading" ? (
          <BusyPanel
            label="uploading // to the archives"
            message={`Sending ${state.file.name} to encrypted storage.`}
          />
        ) : null}

        {state.kind === "parsing" ? (
          <BusyPanel
            label="parsing // the raven is gathering intel"
            message="The Raven is extracting the v3 filing structure — case caption, paragraph numbering, causes of action, targets. This typically takes 20–60 seconds depending on length."
          />
        ) : null}

        {state.kind === "preview" ? (
          <PreviewPanel
            parsed={state.parsed}
            onGoToWarRoom={() => goToWarRoom(state.parsed.case_id)}
            onCancel={reset}
          />
        ) : null}

        {state.kind === "error" ? (
          <ErrorPanel message={state.message} onReset={reset} />
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={onFileInput}
        />
      </main>
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function DropZone({
  file,
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onClick,
  onParse,
  onClear,
}: {
  file: File | null;
  dragOver: boolean;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onClick: () => void;
  onParse: () => void;
  onClear: () => void;
}) {
  return (
    <div className="space-y-4">
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={file ? undefined : onClick}
        className={`group relative cursor-pointer rounded border-2 border-dashed px-8 py-16 text-center backdrop-blur-sm transition-all ${
          dragOver
            ? "border-ember-bright/80 bg-ember/20 shadow-ember-glow"
            : file
              ? "border-ember-bright/40 bg-iron/70"
              : "border-steel-dark bg-iron/50 hover:border-ember-bright/40 hover:bg-iron/70"
        }`}
      >
        {!file ? (
          <>
            <p className="font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">
              // drop zone
            </p>
            <p className="mt-4 font-display text-2xl font-light text-bone">
              Drop a PDF here
            </p>
            <p className="mt-2 font-display text-sm text-bone-dim">
              or click to choose a file
            </p>
            <p className="mt-6 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/70">
              .pdf // max 50 MB
            </p>
          </>
        ) : (
          <>
            <p className="font-display text-base font-light uppercase tracking-[0.18em] text-ember-bright">
              // filing locked in
            </p>
            <p className="mt-4 font-display text-xl font-light text-bone">
              {file.name}
            </p>
            <p className="mt-1 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
              {formatBytes(file.size)} // ready
            </p>
          </>
        )}
      </div>

      {file ? (
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClear}
            className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone"
          >
            ← choose a different file
          </button>
          <button
            type="button"
            onClick={onParse}
            className="border border-ember-bright/60 bg-ember/40 px-8 py-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone transition-colors hover:bg-ember/60 hover:shadow-ember-glow"
          >
            Release the Eater →
          </button>
        </div>
      ) : null}
    </div>
  );
}

function BusyPanel({ label, message }: { label: string; message: string }) {
  return (
    <div className="rounded border border-ember-bright/30 bg-iron/70 px-8 py-10 backdrop-blur-sm">
      <p className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:200ms]" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:400ms]" />
        {label}
      </p>
      <p className="mt-4 max-w-2xl font-display text-sm leading-relaxed text-bone-dim">
        {message}
      </p>
    </div>
  );
}

function PreviewPanel({
  parsed,
  onGoToWarRoom,
  onCancel,
}: {
  parsed: ParseFilingAsyncResult;
  onGoToWarRoom: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded border border-steel-dark bg-iron/70 px-6 py-5 backdrop-blur-sm">
        <p className="font-display text-base font-light uppercase tracking-[0.18em] text-ember-bright">
          // meta committed // paragraphs extracting in background
        </p>
        <h2
          className="mt-2 font-display text-2xl font-light leading-tight text-bone"
          style={{ textShadow: "0 0 16px rgba(195,35,35,0.25)" }}
        >
          {parsed.case.caption}
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 font-display text-base font-light uppercase tracking-[0.12em] text-bone-dim md:grid-cols-4">
          <Field label="case no." value={parsed.case.case_number} />
          <Field label="court" value={parsed.case.court} />
          <Field label="jurisdiction" value={parsed.case.jurisdiction} />
          <Field
            label="filed"
            value={parsed.case.filed_at ?? "unspecified"}
          />
          <Field label="kind" value={parsed.filing.filing_kind} />
          <Field
            label="title"
            value={parsed.filing.title}
            className="md:col-span-2"
          />
          <Field
            label="filed by"
            value={parsed.filing.filed_by ?? "unspecified"}
          />
        </div>
      </div>

      <div className="rounded border border-steel-dark bg-iron/70 px-6 py-5 backdrop-blur-sm">
        <p className="mb-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
          // sections // {parsed.sections.length} blocks
        </p>
        <div className="space-y-2">
          {parsed.sections.map((s) => (
            <div
              key={s.index}
              className="flex items-baseline justify-between gap-4 border-l-2 border-steel-dark/60 pl-3 hover:border-ember-bright/60"
            >
              <div className="min-w-0 flex-1">
                <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
                  {s.section_kind.replace(/_/g, " ")}
                  {s.ordinal !== null ? ` // ordinal ${s.ordinal}` : ""}
                </p>
                <p className="mt-1 truncate font-display text-base font-light text-bone">
                  {s.title ?? "(untitled)"}
                </p>
                {s.legal_basis ? (
                  <p className="mt-1 truncate font-display text-sm text-bone-dim">
                    {s.legal_basis}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded border border-ember-bright/30 bg-ember/10 px-6 py-5 backdrop-blur-sm">
        <p className="font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">
          // case live // paragraphs en route
        </p>
        <p className="mt-2 font-display text-sm leading-relaxed text-bone-dim">
          Meta is committed and paragraph extraction is running. In the war room,
          the docket will prompt you to set each defendant's date of service —
          every defendant's response clock runs from their own service date.
        </p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone"
          >
            ← upload another
          </button>
          <button
            type="button"
            onClick={onGoToWarRoom}
            className="border border-ember-bright/60 bg-ember/40 px-8 py-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone transition-colors hover:bg-ember/60 hover:shadow-ember-glow"
          >
            Enter the war room →
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-bone-mute/70">{label}</p>
      <p className="mt-0.5 truncate text-bone">{value}</p>
    </div>
  );
}

function ErrorPanel({
  message,
  onReset,
}: {
  message: string;
  onReset: () => void;
}) {
  return (
    <div className="rounded border border-ember-bright/40 bg-ember/15 px-6 py-6 backdrop-blur-sm">
      <p className="font-display text-base font-light uppercase tracking-[0.18em] text-ember-bright">
        // something broke
      </p>
      <p className="mt-3 font-display text-xl font-light text-bone">
        The Eater coughed.
      </p>
      <p className="mt-2 font-display text-base text-bone-dim">{message}</p>

      <button
        type="button"
        onClick={onReset}
        className="mt-4 border border-ember-bright/60 bg-iron/70 px-5 py-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:bg-ember/40 hover:shadow-ember-soft"
      >
        Try again →
      </button>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default UploadFiling;
